import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { programLabel, fmtDate, splitTasks, nextBestAction, waitingItems, isEnded, ENDED_LABEL } from "@/pages/portal/portalUtils";
import { UpcomingTrainingCard } from "@/pages/events/EventsTraining";
import { ArrowRight, CalendarCheck, CheckCircle2, ClipboardList, Clock, Hourglass, MessageSquare, Sparkles, Users, Video, Compass, Flag } from "lucide-react";

const Card = ({ title, Icon, action, children, testId, className = "" }) => (
  <section className={`rounded-2xl border border-slate-200 bg-white p-5 tr-animate-in ${className}`} data-testid={testId}>
    <div className="flex items-center justify-between gap-2 mb-3">
      <h2 className="font-display text-base font-semibold text-slate-900 flex items-center gap-2"><Icon className="w-4 h-4 text-[#006090]" />{title}</h2>
      {action}
    </div>
    {children}
  </section>
);

const LinkBtn = ({ onClick, children, testId }) => (
  <button onClick={onClick} data-testid={testId} className="text-xs font-semibold text-[#006090] hover:text-[#004E75] inline-flex items-center gap-1 group">
    {children}<ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
  </button>
);

const Empty = ({ children, testId }) => <p className="text-sm text-slate-400 py-2" data-testid={testId}>{children}</p>;

export default function PortalHome({ summary, goTo }) {
  const [roadmap, setRoadmap] = useState(null);
  const [tasks, setTasks] = useState(null);
  const [meetings, setMeetings] = useState(null);
  const [team, setTeam] = useState(null);
  useEffect(() => {
    api.get("/portal/roadmap").then((r) => setRoadmap(r.data)).catch(() => setRoadmap({ enrollment: null }));
    api.get("/portal/tasks").then((r) => setTasks(r.data.tasks || [])).catch(() => setTasks([]));
    api.get("/portal/meetings").then((r) => setMeetings(r.data)).catch(() => setMeetings({ upcoming: [], past: [] }));
    api.get("/team/my-team").then((r) => setTeam(r.data)).catch(() => setTeam({}));
  }, []);
  const loading = roadmap === null || tasks === null || meetings === null;
  const upcoming = (meetings?.upcoming || [])[0] || null;

  return (
    <div className="space-y-6" data-testid="portal-home">
      <div className="tr-animate-in">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#0D9488]">Candidate Portal</p>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mt-1" data-testid="portal-welcome">Welcome, {summary.name?.split(" ")[0] || "Candidate"}</h1>
      </div>
      {loading ? <div className="h-48 rounded-2xl bg-white animate-pulse" /> : (
        <>
          <PhaseAdvance roadmap={roadmap} goTo={goTo} />
          <StatusHero roadmap={roadmap} goTo={goTo} />
          <UpcomingTrainingCard onOpenAll={() => goTo("events")} />
          <NextBestAction nba={nextBestAction({ roadmap, tasks, upcoming })} goTo={goTo} />
          <div className="grid lg:grid-cols-2 gap-5">
            <CurrentTasks tasks={tasks} goTo={goTo} />
            <WaitingOnDistrict tasks={tasks} roadmap={roadmap} goTo={goTo} />
            <UpcomingAppointment m={upcoming} goTo={goTo} />
            <DistrictTeam team={team} summary={summary} goTo={goTo} />
          </div>
          <RecentProgress tasks={tasks} roadmap={roadmap} />
        </>
      )}
    </div>
  );
}

function PhaseAdvance({ roadmap, goTo }) {
  const phases = roadmap.phases || [];
  const idx = phases.findIndex((p) => p.is_current);
  const key = roadmap.enrollment ? `tr_portal_phase_${roadmap.enrollment.program}_${roadmap.enrollment.version_label || ""}` : null;
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (!key || idx < 0) return;
    const seen = localStorage.getItem(key);
    if (seen !== null && Number(seen) < idx) setShow(true);
    if (seen === null || Number(seen) < idx) localStorage.setItem(key, String(idx));
  }, [key, idx]);
  if (!show || isEnded(roadmap.enrollment)) return null;
  const cur = phases[idx];
  return (
    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 flex flex-col sm:flex-row sm:items-center gap-3 tr-animate-in" data-testid="portal-phase-advance">
      <Sparkles className="w-6 h-6 text-emerald-600 shrink-0" />
      <div className="flex-1">
        <p className="font-display font-bold text-emerald-900">You've reached {cur.name}</p>
        <p className="text-sm text-emerald-800">You've completed {phases[idx - 1]?.name || "the previous stage"} and are now entering {cur.name}.</p>
      </div>
      <Button size="sm" variant="outline" className="rounded-full" onClick={() => { setShow(false); goTo("journey", "roadmap"); }} data-testid="portal-phase-advance-cta">View My Next Steps</Button>
    </div>
  );
}

function StatusHero({ roadmap, goTo }) {
  const enr = roadmap.enrollment;
  const phases = roadmap.phases || [];
  const ci = phases.findIndex((p) => p.is_current);
  const current = phases[ci];
  const nextPhase = ci >= 0 ? phases[ci + 1] : null;
  const ended = isEnded(enr);
  const pct = roadmap.configured === false ? 0 : (roadmap.progress || 0);
  const gate = roadmap.career_gate || roadmap.reserve_gate;
  return (
    <div className="rounded-2xl bg-[#013A5A] text-white p-6 sm:p-7 relative overflow-hidden tr-animate-in" data-testid="portal-status-hero">
      <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-[#0D9488]/25 blur-3xl" />
      <p className="relative text-[#5EEAD4] text-xs font-bold uppercase tracking-[0.18em] mb-4">Your Path to Agency Ownership</p>
      <div className="relative grid sm:grid-cols-3 gap-5">
        <div>
          <p className="text-[11px] uppercase tracking-wider text-[#5EEAD4] font-semibold">Current Program</p>
          <p className="font-display text-xl font-bold mt-1" data-testid="portal-current-program">{programLabel(enr) || "Not yet assigned"}</p>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-wider text-[#5EEAD4] font-semibold">Current Stage</p>
          <p className="font-display text-xl font-bold mt-1" data-testid="portal-current-stage">{ended ? ENDED_LABEL[enr.status] : enr ? (current?.name || "—") : "Getting started"}</p>
          {enr && !ended && <p className="text-xs text-white/70 mt-1" data-testid="portal-next-milestone">Next milestone: {nextPhase?.name || "Program completion"}</p>}
        </div>
        <div className="sm:text-right">
          <p className="text-[11px] uppercase tracking-wider text-[#5EEAD4] font-semibold">Official Progress</p>
          <p className="font-display text-4xl font-bold leading-none mt-1" data-testid="portal-official-progress">{enr ? `${pct}%` : "—"}</p>
        </div>
      </div>
      {enr ? (
        <div className="relative mt-5">
          <div className="h-2 bg-white/15 rounded-full overflow-hidden"><div className="h-full bg-[#0D9488] rounded-full transition-[width] duration-700" style={{ width: `${pct}%` }} /></div>
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-white/70">
            <span data-testid="portal-req-count">{roadmap.required_complete} of {roadmap.required_total} program requirements complete</span>
            {gate && <span className="inline-flex items-center gap-1" data-testid="portal-hero-gate"><Flag className="w-3 h-3" />{gate.ready ? "Ready for the next milestone" : `${gate.blocking?.length || 0} item(s) before your next milestone`}</span>}
          </div>
          <button onClick={() => goTo("journey", "roadmap")} data-testid="portal-hero-journey" className="mt-4 text-sm font-semibold text-[#5EEAD4] hover:text-white inline-flex items-center gap-1">See your full journey <ArrowRight className="w-4 h-4" /></button>
        </div>
      ) : <p className="relative mt-4 text-sm text-white/70" data-testid="portal-hero-empty">Your recruiter will set up your program roadmap after your first conversations.</p>}
    </div>
  );
}

function NextBestAction({ nba, goTo }) {
  const act = () => (nba.href ? window.open(nba.href, "_blank", "noopener") : goTo(...nba.go));
  const waiting = nba.kind === "waiting" || nba.kind === "ended";
  return (
    <div className={`rounded-2xl border-2 p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4 tr-animate-in ${waiting ? "border-emerald-200 bg-emerald-50/60" : "border-[#006090] bg-white shadow-[0_8px_30px_-12px_rgba(0,96,144,0.35)]"}`} data-testid="portal-next-best-action" data-kind={nba.kind}>
      <span className={`w-12 h-12 shrink-0 rounded-xl flex items-center justify-center ${waiting ? "bg-emerald-100 text-emerald-700" : "bg-[#006090] text-white"}`}>{waiting ? <Sparkles className="w-6 h-6" /> : <Compass className="w-6 h-6" />}</span>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-bold uppercase tracking-wider text-[#006090]">Your Next Step</p>
        <p className="font-display text-lg sm:text-xl font-bold text-slate-900 mt-0.5" data-testid="portal-nba-title">{nba.title}</p>
        <p className="text-sm text-slate-600 mt-0.5 line-clamp-2">{nba.detail}</p>
      </div>
      <Button onClick={act} data-testid="portal-nba-cta" className="shrink-0 bg-[#006090] hover:bg-[#004E75] rounded-full px-5">{nba.cta}<ArrowRight className="w-4 h-4 ml-1.5" /></Button>
    </div>
  );
}

function TaskRow({ t, onClick, sub }) {
  return (
    <button onClick={onClick} data-testid={`portal-home-task-${t.id}`} className="w-full text-left rounded-xl bg-slate-50 hover:bg-[#006090]/5 border border-transparent hover:border-[#006090]/20 px-3 py-2.5 transition-colors">
      <p className="text-sm font-medium text-slate-800 line-clamp-1">{t.display_title || t.title}</p>
      <p className="text-xs text-slate-500 mt-0.5">{sub}</p>
    </button>
  );
}

function CurrentTasks({ tasks, goTo }) {
  const { mine } = splitTasks(tasks);
  const label = (t) => [t.due_date ? `Due ${fmtDate(t.due_date)}` : "No deadline", t.status === "in_progress" ? "In Process" : "Not Started"].join(" · ");
  return (
    <Card title="Your Current Tasks" Icon={ClipboardList} testId="portal-home-current-tasks" action={<LinkBtn onClick={() => goTo("tasks")} testId="portal-home-all-tasks">All tasks</LinkBtn>}>
      {mine.length === 0 ? <Empty testId="portal-home-tasks-empty">You're caught up — there are no actions waiting on you right now.</Empty> : (
        <div className="space-y-2">{mine.slice(0, 4).map((t) => <TaskRow key={t.id} t={t} sub={label(t)} onClick={() => goTo("tasks", null, { taskId: t.id })} />)}
          {mine.length > 4 && <p className="text-xs text-slate-400 pt-1">+{mine.length - 4} more</p>}</div>
      )}
    </Card>
  );
}

function WaitingOnDistrict({ tasks, roadmap, goTo }) {
  const items = waitingItems(tasks, roadmap);
  return (
    <Card title="Waiting on Your District Team" Icon={Hourglass} testId="portal-home-waiting">
      {items.length === 0 ? <Empty testId="portal-home-waiting-empty">Nothing pending with your district team.</Empty> : (
        <div className="space-y-2">{items.slice(0, 4).map((i) => (
          <div key={i.id} className="flex items-start gap-2.5 rounded-xl bg-amber-50/60 border border-amber-100 px-3 py-2.5" data-testid={`portal-waiting-${i.id}`}>
            <Clock className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <div className="min-w-0"><p className="text-sm font-medium text-slate-800 line-clamp-1">{i.title}</p><p className="text-xs text-slate-500">{i.sub}</p></div>
          </div>))}
          {items.length > 4 && <LinkBtn onClick={() => goTo("tasks")} testId="portal-home-waiting-more">View all ({items.length})</LinkBtn>}
        </div>
      )}
    </Card>
  );
}

function UpcomingAppointment({ m, goTo }) {
  return (
    <Card title="Upcoming Appointment" Icon={CalendarCheck} testId="portal-upcoming-interview" action={<LinkBtn onClick={() => goTo("appointments", "upcoming")} testId="portal-view-appointment">Appointments</LinkBtn>}>
      {!m ? (
        <div><Empty testId="portal-home-no-appointment">No upcoming appointments.</Empty>
          <Button size="sm" variant="outline" className="mt-1 rounded-full" onClick={() => goTo("appointments", "book")} data-testid="portal-home-book">Book a Meeting</Button></div>
      ) : (
        <div>
          <p className="font-semibold text-slate-900">{m.appointment_type}</p>
          <p className="text-sm text-slate-600 mt-0.5">{m.start ? new Date(m.start).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : ""}{m.timezone ? ` · ${m.timezone}` : ""}</p>
          <p className="text-sm text-slate-500">with {m.host_name || "your team"}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {m.meeting_link && <a href={m.meeting_link} target="_blank" rel="noreferrer"><Button size="sm" className="bg-[#006090] hover:bg-[#004E75] rounded-full" data-testid="portal-home-join"><Video className="w-3.5 h-3.5 mr-1" />Join Teams</Button></a>}
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => goTo("appointments", "upcoming")} data-testid="portal-home-view-appt">View</Button>
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => goTo("appointments", "upcoming")} data-testid="portal-home-reschedule">Reschedule</Button>
          </div>
        </div>
      )}
    </Card>
  );
}

function DistrictTeam({ team, summary, goTo }) {
  const rec = team?.recruiter_card;
  const support = team?.support_contacts || [];
  const recAreas = team?.recruiter_areas || [];
  const others = support.length ? [] : [...(team?.leadership || []), ...(team?.all_members || [])].filter((m, i, a) => m.id !== rec?.id && a.findIndex((x) => x.id === m.id) === i).slice(0, 3);
  const message = () => window.dispatchEvent(new Event("tr-open-support"));
  return (
    <Card title="Your District Team" Icon={Users} testId="portal-home-team" action={<LinkBtn onClick={() => goTo("support", "team")} testId="portal-home-team-all">Who to contact</LinkBtn>}>
      <p className="text-xs text-slate-500 -mt-1 mb-3" data-testid="portal-district">{team?.district?.name || summary.district_name || "Your district"}</p>
      <div className="flex items-center gap-3 rounded-xl border border-[#0D9488]/25 bg-[#0D9488]/5 p-3" data-testid="portal-recruiter">
        <Avatar m={rec} name={rec?.name || summary.recruiter_name} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-900">{rec?.name || summary.recruiter_name || "Being assigned"}</p>
          <p className="text-xs text-slate-500">Primary Recruiter{rec?.title ? ` · ${rec.title}` : ""}</p>
          {recAreas.length > 0 && <p className="text-xs text-[#006090] mt-0.5" data-testid="portal-recruiter-areas">Also helps with: {recAreas.join(", ")}</p>}
        </div>
        <Button size="sm" variant="outline" className="rounded-full shrink-0" onClick={message} data-testid="portal-home-message"><MessageSquare className="w-3.5 h-3.5 mr-1" />Contact</Button>
      </div>
      {support.length > 0 && <div className="mt-3 space-y-2.5" data-testid="portal-home-support">{support.map(({ member: m, areas }) => (
        <div key={m.id} className="flex items-start gap-3" data-testid={`portal-home-support-${m.id}`}>
          <Avatar m={m} name={m.name} small />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-slate-800 line-clamp-1">{m.name}</p>
            <p className="text-xs text-slate-500">{areas.join(" · ")}</p>
          </div>
          {m.phone && <a href={`tel:${m.phone.replace(/[^0-9+]/g, "")}`} className="text-xs font-medium text-[#006090] shrink-0" data-testid={`portal-home-support-call-${m.id}`}>Call</a>}
        </div>))}</div>}
      {others.length > 0 && <div className="mt-3 space-y-2">{others.map((m) => (
        <div key={m.id} className="flex items-center gap-3" data-testid={`portal-home-team-${m.id}`}>
          <Avatar m={m} name={m.name} small />
          <div className="min-w-0"><p className="text-sm font-medium text-slate-800 line-clamp-1">{m.name}</p><p className="text-xs text-slate-500 line-clamp-1">{m.title}</p></div>
        </div>))}</div>}
    </Card>
  );
}

function Avatar({ m, name, small }) {
  const sz = small ? "w-8 h-8 text-xs" : "w-10 h-10 text-sm";
  const initials = (name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("");
  return m?.photo_url ? <img src={m.photo_url} alt={name} className={`${sz} rounded-full object-cover shrink-0`} />
    : <span className={`${sz} rounded-full bg-[#013A5A] text-white font-semibold flex items-center justify-center shrink-0`}>{initials}</span>;
}

function RecentProgress({ tasks, roadmap }) {
  const items = [
    ...(roadmap.requirements || []).filter((r) => r.status === "complete" && r.completed_at).map((r) => ({ id: `r-${r.requirement_id}`, title: r.title, at: r.completed_at, kind: "Milestone verified" })),
    ...tasks.filter((t) => t.is_done && t.completed_at && t.status === "completed").map((t) => ({ id: t.id, title: t.display_title || t.title, at: t.completed_at, kind: t.completed_by_district ? "Completed by District Team" : "Task completed" })),
  ].sort((a, b) => b.at.localeCompare(a.at)).slice(0, 5);
  return (
    <Card title="Recent Progress" Icon={CheckCircle2} testId="portal-home-recent">
      {items.length === 0 ? <Empty testId="portal-home-recent-empty">Your completed milestones and tasks will appear here.</Empty> : (
        <ol className="relative border-l border-emerald-200 ml-1.5 space-y-3">{items.map((i) => (
          <li key={i.id} className="pl-4 relative" data-testid={`portal-recent-${i.id}`}>
            <span className="absolute -left-[5px] top-1.5 w-2.5 h-2.5 rounded-full bg-emerald-500" />
            <p className="text-sm font-medium text-slate-800">{i.title}</p>
            <p className="text-xs text-slate-500">{i.kind} · {fmtDate(i.at)}</p>
          </li>))}</ol>
      )}
    </Card>
  );
}
